import React, { useState, useEffect, useRef } from 'react'
import Editor from '@monaco-editor/react'
import { Alert, Button } from 'antd'
import './JsonEditorField.css'

/**
 * JSON Editor component with validation for form fields
 * @param {Object} props
 * @param {string} props.value - The JSON string value from form
 * @param {Function} props.onChange - Callback when value changes
 * @param {string} props.placeholder - Placeholder text
 */
const JsonEditorField = ({ value, onChange, placeholder }) => {
  const [editorValue, setEditorValue] = useState('')
  const [error, setError] = useState(null)
  const editorRef = useRef(null)
  const previousValueRef = useRef(undefined)

  // Parse and format value whenever it changes from outside
  useEffect(() => {
    // Only update if the value actually changed from outside
    // This prevents overwriting user edits
    if (value !== previousValueRef.current) {
      previousValueRef.current = value

      if (value) {
        try {
          const parsed = typeof value === 'string' ? JSON.parse(value) : value
          const formatted = JSON.stringify(parsed, null, 2)
          setEditorValue(formatted)
          setError(null)
        } catch (e) {
          // If it's a string but not valid JSON, show it as-is
          if (typeof value === 'string') {
            setEditorValue(value)
            setError(`Invalid JSON: ${e.message}`)
          } else {
            setEditorValue('')
            setError(null)
          }
        }
      } else {
        setEditorValue('')
        setError(null)
      }
    }
  }, [value])

  // Validate and handle editor changes
  const handleEditorChange = (newValue) => {
    setEditorValue(newValue || '')

    if (!newValue || newValue.trim() === '') {
      setError(null)
      onChange('')
      return
    }

    try {
      JSON.parse(newValue)
      setError(null)
      onChange(newValue)
    } catch (e) {
      setError(`Invalid JSON: ${e.message}`)
      // Still call onChange so the form can handle validation
      onChange(newValue)
    }
  }

  const handleEditorDidMount = (editor) => {
    editorRef.current = editor
  }

  const handleInitialize = () => {
    const emptyObj = {}
    const formatted = JSON.stringify(emptyObj, null, 2)
    setEditorValue(formatted)
    setError(null)
    onChange(formatted)
  }

  const handleFormat = () => {
    if (editorValue) {
      try {
        const parsed = JSON.parse(editorValue)
        const formatted = JSON.stringify(parsed, null, 2)
        setEditorValue(formatted)
        setError(null)
        onChange(formatted)
      } catch (e) {
        setError(`Cannot format invalid JSON: ${e.message}`)
      }
    }
  }

  return (
    <div className="json-editor-field">
      {error && (
        <Alert
          message="JSON Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      <div className="json-editor-container">
        {editorValue ? (
          <>
            <div style={{ marginBottom: 8 }}>
              <Button size="small" onClick={handleFormat} disabled={!editorValue}>
                Format JSON
              </Button>
            </div>
            <Editor
              height="400px"
              defaultLanguage="json"
              value={editorValue}
              onChange={handleEditorChange}
              onMount={handleEditorDidMount}
              theme="vs-light"
              options={{
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 14,
                lineNumbers: 'on',
                roundedSelection: false,
                automaticLayout: true,
                formatOnPaste: true,
                formatOnType: true,
                tabSize: 2,
              }}
            />
          </>
        ) : (
          <div className="json-editor-empty">
            <p>{placeholder || 'Enter valid JSON or start editing...'}</p>
            <button
              type="button"
              className="json-editor-init-button"
              onClick={handleInitialize}
            >
              Initialize JSON Object
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default JsonEditorField
